<template>
  <LayoutOverlay class="modal-edit-room" @closeOverlay="closeModal()">
    <div class="modal-edit-room-container">
      <div class="header">
        <h2>
          {{ t('general__text__update') + ' ' + t('general__text__information').toLowerCase() }}
        </h2>
        <button class="header__close-button" @click="closeModal()">
          <IconXCircleMedium class="header__close-button__icon" />
        </button>
      </div>
      <DividerHorizontal />
      <div class="content">
        <TextFieldStandard
          :hideIconX="true"
          :infoText="t('info__edit-room-name')"
          :label="t('general__text__room-name')"
          :placeHolderText="roomName ?? t('general__text__room-name')"
          @emitInput="updateName"
        />
        <TextFieldStandard
          :hideIconX="true"
          :infoText="t('info__edit-room-number')"
          :label="t('general__text__room-number')"
          :placeHolderText="roomLabel ?? t('general__text__room-number')"
          @emitInput="updateLabel"
        />
      </div>
      <div class="content">
        <TextFieldStandard
          :error="stringInput"
          :error-message="t('general__text__enter-an-integer')"
          :hideIconX="true"
          :infoText="t('info__edit-room-capacity')"
          :label="t('general__data__room__capacity-only')"
          :placeHolderText="roomCapacity?.toString() ?? t('general__data__room__capacity-only')"
          @emitInput="updateCapacity"
        />
        <div class="dropdown-box">
          <div class="dropdown-box__label">
            {{ t('general__text__used-room-type') }}
            <TooltipStandardV2
              :text="t('info__edit-room-type')"
              :arrowPosition="'bottom-center'"
              :vertical-offset="2"
            >
              <IconInfoCircle></IconInfoCircle>
            </TooltipStandardV2>
          </div>
          <DropdownStandard
            :title="selectedRoomType ?? t('general__text__room-types')"
            :hasSearch="false"
            :options="roomUsedTypeOptions"
            @select="updateUsedRoomType"
          />
        </div>
      </div>
      <div class="checkbox-container">
        <CheckBoxStandard
          :checked="dataQualityChecked"
          :title="t('info__data-update-quality')"
          :disabled="false"
          @valueChanged="updateDataQuality"
        ></CheckBoxStandard>
        <div class="checkbox-container__info">
          <TooltipStandardV2
            :text="t('info__edit-room-quality-update')"
            :arrowPosition="'bottom-center'"
            :vertical-offset="2"
          >
            <IconInfoCircle></IconInfoCircle>
          </TooltipStandardV2>
        </div>
      </div>
      <div class="footer">
        <ButtonOutline
          class="button"
          :buttonText="t('general__text__cancel')"
          size="small"
          @onClicked="closeModal()"
        ></ButtonOutline>
        <ButtonPrimary
          class="button"
          :buttonText="t('general__text__update-room')"
          :disabled="disableSaveButton()"
          size="small"
          @onClicked="save()"
        ></ButtonPrimary>
      </div>
    </div>
  </LayoutOverlay>
</template>
<script lang="ts" setup>
import { useI18n } from 'vue-i18n'
import ButtonOutline from '@/design-system/atoms/buttons/ButtonOutline.vue'
import ButtonPrimary from '@/design-system/atoms/buttons/ButtonPrimary.vue'
import CheckBoxStandard from '@/design-system/atoms/CheckBoxStandard.vue'
import DropdownStandard, { optionType } from '@/design-system/molecules/DropdownStandard.vue'
import DividerHorizontal from '@/design-system/atoms/DividerHorizontal.vue'
import IconInfoCircle from '@/design-system/assets/icons/IconInfoCircle.vue'
import IconXCircleMedium from '@/design-system/assets/icons/IconXCircleMedium.vue'
import TextFieldStandard from '@/design-system/atoms/TextFieldStandard.vue'
import LayoutOverlay from '@/design-system/templates/LayoutOverlay.vue'
import TooltipStandardV2 from '@/design-system/atoms/TooltipStandardV2.vue'
import { onMounted, ref } from 'vue'
import { COCLASSES_AH } from '@/interfaces/coclass.interface'
import { UpdateCoreEntityDto } from '@/services/core.service'
import { useSelectionStore } from '@/pinia/selection'
import { storeToRefs } from 'pinia'
import { Classification } from '@/interfaces/core.interface'
import { useCoreEntityStore } from '@/pinia/coreEntity'
import { isInteger } from '@/utils/formValidation'
import { useStore } from 'vuex'
import * as Sentry from '@sentry/vue'

const emits = defineEmits(['closeEditRoomModal'])
const { t } = useI18n()
const store = useStore()
const selectionStore = useSelectionStore()
const coreEntityStore = useCoreEntityStore()
const { currentEntity } = storeToRefs(selectionStore)
const roomName = ref<string | undefined>(currentEntity.value?.nameTenant)
const roomLabel = ref<string | undefined>(currentEntity.value?.labelTenant)
const roomCapacity = ref<number | undefined>(currentEntity.value?.properties?.capacity)
const roomUsedType = ref<optionType>()
const selectedRoomType = ref(currentEntity.value?.properties?.classification?.USED?.name)
const stringInput = ref(false)
const dataQualityChecked = ref(false)

const roomUsedTypeOptions = COCLASSES_AH.map((coclass) => ({
  id: coclass.coclass,
  name: coclass.description,
  selected: false,
  disabled: false
})).toSorted((a, b) => a.name.localeCompare(b.name))
function closeModal() {
  emits('closeEditRoomModal', false)
}
function disableSaveButton() {
  if (!formHasChanged()) return true
  return (
    !dataQualityChecked.value ||
    (!roomName.value && !roomLabel.value && !roomCapacity.value && !selectedRoomType.value)
  )
}
function formHasChanged(): boolean {
  return (
    roomName.value !== currentEntity.value?.nameTenant ||
    roomLabel.value !== currentEntity.value?.labelTenant ||
    roomCapacity.value !== currentEntity.value?.properties?.capacity ||
    selectedRoomType.value !== currentEntity.value?.properties?.classification?.USED?.name
  )
}
async function save() {
  if (!currentEntity.value) return
  const classification: Partial<Record<'PLANNED' | 'USED', Classification>> = {}
  if (roomUsedType.value) {
    classification.USED = {
      system: 'CO_CLASS',
      classCode: roomUsedType.value.id,
      name: roomUsedType.value.name
    }
  }
  let requestBody: UpdateCoreEntityDto = {
    type: 'SPACE',
    nameTenant: roomName.value ?? currentEntity.value?.nameTenant,
    labelTenant: roomLabel.value ?? currentEntity.value?.labelTenant,
    properties: {
      ...currentEntity.value?.properties,
      capacity: roomCapacity.value ?? currentEntity.value?.properties?.capacity,
      classification: classification ?? currentEntity.value?.properties?.classification?.USED
    }
  }
  try {
    await coreEntityStore.updateById(currentEntity.value?.id, requestBody)
    store.dispatch('CHANGE_ROOM', { roomId: currentEntity.value?.id })
    store.commit('setInfoMessage', {
      type: 'success',
      message: t('general__text__update-succeded')
    })
    closeModal()
  } catch (error) {
    Sentry.captureException(error)
    store.commit('setInfoMessage', {
      type: 'error',
      message: t('general__text__update-failed')
    })
    return
  }
}
function updateDataQuality() {
  if (!dataQualityChecked.value) {
    dataQualityChecked.value = true
    localStorage.setItem('dataQualityChecked', 'true')
  } else {
    dataQualityChecked.value = false
    localStorage.setItem('dataQualityChecked', 'false')
  }
}
function updateName(value: string | number) {
  if (typeof value === 'number') {
    roomName.value = value.toString()
    return
  }
  if (value.length === 0) {
    roomName.value = currentEntity.value?.nameTenant
  } else {
    roomName.value = value
  }
}
function updateLabel(value: string | number) {
  if (typeof value === 'number') {
    roomLabel.value = value.toString()
    return
  }
  if (value.length === 0) {
    roomLabel.value = currentEntity.value?.labelTenant
  } else roomLabel.value = value
}
function updateCapacity(value: string | number) {
  if (typeof value === 'number') {
    roomCapacity.value = value
    stringInput.value = false
    return
  }
  stringInput.value = !isInteger(value)
  if (stringInput.value) {
    // refactor if enabling sending empty capacity
    roomCapacity.value = currentEntity.value?.properties?.capacity
    return
  }
  roomCapacity.value = parseInt(value)
}
function updateUsedRoomType(value: string) {
  roomUsedType.value = roomUsedTypeOptions.find((option) => option.id === value)
  if (roomUsedType.value) selectedRoomType.value = roomUsedType.value.name
}
onMounted(() => {
  if (localStorage.getItem('dataQualityChecked') === 'true') {
    dataQualityChecked.value = true
  }
})
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/font-mixins.scss';
@use '@/design-system/design-tokens/mixins.scss';
.modal-edit-room-container {
  background-color: variables.$color-background;
  border-radius: variables.$border-radius-small;
  color: variables.$color-greyscale-content;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  left: 50%;
  max-height: 650px;
  padding: 24px;
  position: absolute;
  top: 40%;
  transform: translate(-50%, -50%);
  width: 700px;
  .checkbox-container {
    align-items: center;
    display: flex;
    gap: 4px;
    padding: 16px 0;
    &__info {
      width: fit-content;
      svg {
        width: variables.$tooltip-icon-size;
      }
    }
  }
  .content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 32px;
    padding: 12px 0;
    .dropdown-box {
      display: flex;
      flex-direction: column;
      gap: variables.$gap-large;
      &__label {
        @include font-mixins.desktop-bold-paragraph;
        color: variables.$color-greyscale-content;
        display: flex;
        flex-direction: row;
        width: fit-content;
        gap: variables.$gap-large;
        svg {
          width: variables.$tooltip-icon-size;
        }
      }
    }

    & > * {
      width: 100%;
    }
  }
  .footer {
    align-items: flex-end;
    display: flex;
    flex-direction: row;
    gap: variables.$gap-large;
    justify-content: flex-end;
  }
  h2 {
    @include font-mixins.desktop-bold-h4;
    color: variables.$color-greyscale-content;
    padding-bottom: 16px;
    margin: 0;
  }
  .header {
    display: flex;
    justify-content: space-between;
    &__close-button {
      all: unset;
      height: 40px;
      width: 40px;
      &:focus {
        outline: revert;
      }
      &__icon {
        color: variables.$color-primary;
        cursor: pointer;
      }
    }
  }
}

@media screen and (max-width: 1024px) {
  .modal-edit-room {
    @include mixins.scrollbar-vertical;
    overflow-y: auto;
  }
  .modal-edit-room-container {
    display: block;
    max-height: fit-content;
    max-width: 90vw;
    left: 5%;
    top: 32px;
    transform: unset;
    margin-bottom: 32px;
    .content {
      display: grid;
      grid-template-columns: 1fr;
    }
  }
}
</style>
